import { css } from '@emotion/react';
import {
  faEllipsisV, faExclamationCircle, faExternalLink, faEye, faEyeSlash, faMapMarkerEdit, faRoute, faSearch, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type ReactNode, type Ref, useMemo,
} from 'react';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  LabelInShapeComponent, Shape,
} from '~/_shared/baseComponents/labelInShape/labelInShapeComponent';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  createColor, guaranteeHash,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type Theme } from '~/_shared/themes/theme.model';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import {
  isDriveTimePolygon, isGroupRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { getFontAndBorderColorOnBackground } from '~/_shared/utils/colors/colors.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { dtpClipPath } from '~/proximity/dtpShape.constant';

const ICON_SIZE = 16;
const VERTICAL_PADDING = 12;

const wrapperStyle = (theme: Theme) => css({
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  cursor: 'default',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '10px 0',
  width: '100%',
  position: 'relative',
});

const errorStyle = (theme: Theme) => css({
  alignItems: 'center',
  color: theme.textColors.danger,
  display: 'flex',
  fontSize: 12,
  gap: 10,
  padding: '0 12px 0 10px',
});

const headerStyle = ({ theme, disabled }: ThemeProps<{disabled: boolean}>) => css({
  alignItems: 'center',
  boxSizing: 'border-box',
  color: disabled ? theme.textColors.disabled : theme.textColors.primary,
  display: 'flex',
  padding: `0 ${VERTICAL_PADDING}px 0 5px`,
});

const toggleRadiusButtonStyle = css({
  flexShrink: 0,
  fontSize: `${ICON_SIZE}px`,
  marginRight: 12,
  width: 20, // so the alternating icons are not jumpy due to different width
});

const nameWrapperStyle = css({
  overflow: 'hidden',
  display: 'flex',
  padding: '0 8px',
});

const namePrefixStyle = css({
  fontSize: '14px',
  fontWeight: 700,
  marginRight: 4,
  textTransform: 'uppercase',
});

const nameStyle = css({
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'left',
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const infoWrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 12,
  marginLeft: 'auto',
});

const estimationStyle = css({
  whiteSpace: 'nowrap',
  fontSize: '14px',
  fontWeight: 600,
});

const settingsButtonStyle = css({
  background: 'none',
  border: 'none',
  fontSize: `${ICON_SIZE}px`,
});

export type ProximityListingItemContextCallbacks = Readonly<{
  hideSettingsMenu?: () => void;
  onExportClick?: () => void;
  onExportContainedBoundaries?: () => void;
  onProximityEdit?: () => void;
  onProximityGetRoute?: () => void;
  onProximityZoom?: () => void;
  onRemoveProximity?: () => void;
  openSettingsMenu?: () => void;
}>;

type ProximityListingItemProps = Readonly<{
  isDisabled: boolean;
  isLoading: boolean;
  error: ReactNode;
  distance: ReactNode;
  driveTimeLabels: ReactNode;
  isHidden: boolean;
  isSettingsMenuVisible: boolean;
  item: Proximity;
  ordinalPosition: number;

  contextCallbacks: ProximityListingItemContextCallbacks;
  toggleVisibility?: () => void;
}>;

export const ProximityListingItemComponent: FC<ProximityListingItemProps> = props => {
  const theme = useTheme();
  const [t] = useTranslation();
  const disabled = !!props.error || props.isDisabled;

  const { onRemoveProximity, onProximityZoom, onProximityEdit,
    onProximityGetRoute, openSettingsMenu, hideSettingsMenu,
    onExportContainedBoundaries, onExportClick } = props.contextCallbacks;

  return (
    <div css={wrapperStyle(theme)}>
      {props.isLoading && (
        <TooltipComponent tooltipContent={t('Loading...')}>
          <OverlayLoaderComponent loaderSize={ICON_SIZE + VERTICAL_PADDING} />
        </TooltipComponent>
      )}
      <div css={headerStyle({ theme, disabled })}>
        {props.toggleVisibility ? (
          <HoverableIconComponent
            css={toggleRadiusButtonStyle}
            hoverColor={theme.iconColors.contrast}
            icon={props.isHidden || disabled ? faEyeSlash : faEye}
            title={t('Toggle Visibility')}
            onClick={props.toggleVisibility}
          />
        ) : (
          <HoverableIconComponent
            css={toggleRadiusButtonStyle}
            hoverColor={theme.iconColors.contrast}
            icon={props.isHidden || disabled ? faEyeSlash : faEye}
          />
        )}

        <StepNumber
          number={props.ordinalPosition}
          color={props.item.styles.color}
          round={props.item.type !== ProximityType.DriveTimePolygon}
        />

        <div css={nameWrapperStyle}>
          {isGroupRadius(props.item) && (
            <strong css={namePrefixStyle}>
              {t('Group')}:
            </strong>
          )}
          <p css={nameStyle}>{props.item.name}</p>
        </div>

        <div css={infoWrapperStyle} >
          <div css={estimationStyle}>
            {props.distance}
            {props.driveTimeLabels}
          </div>

          <ContextMenuComponent
            isFixed
            maxWidth={280}
            renderMenuContent={() => (
              <>
                {onProximityEdit && (
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      icon={faMapMarkerEdit}
                      onClick={onProximityEdit}
                      isDisabled={disabled}
                    >
                      {t('Edit Proximity')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                )}

                <ContextMenuItemCoreComponent>
                  <ContextMenuDefaultItem
                    onClick={onProximityZoom}
                    icon={faSearch}
                    isDisabled={disabled}
                  >
                    {t('Zoom To Proximity')}
                  </ContextMenuDefaultItem>
                </ContextMenuItemCoreComponent>

                {onExportClick && (
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      onClick={onExportClick}
                      icon={faExternalLink}
                      isDisabled={disabled}
                    >
                      {t('Export Locations')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                )}

                {onExportContainedBoundaries && (
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      onClick={onExportContainedBoundaries}
                      icon={faExternalLink}
                      isDisabled={disabled}
                    >
                      {t('Export Contained Boundaries')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                )}

                <ContextMenuItemCoreComponent>
                  <ContextMenuDefaultItem
                    icon={faRoute}
                    isDisabled={disabled}
                    onClick={onProximityGetRoute}
                  >
                    {t('Routing / Directions')}
                  </ContextMenuDefaultItem>
                </ContextMenuItemCoreComponent>

                <ContextMenuItemCoreComponent>
                  <ContextMenuDefaultItem
                    icon={faTrash}
                    isDestructive
                    onClick={onRemoveProximity}
                  >
                    {isDriveTimePolygon(props.item) ? t('Delete Polygon') : t('Delete Radius')}
                  </ContextMenuDefaultItem>
                </ContextMenuItemCoreComponent>
              </>
            )}
            isVisible={props.isSettingsMenuVisible}
            onHide={hideSettingsMenu}
            preferredPlacement="bottom-end"
          >
            {ref => (
              <button
                ref={ref as Ref<HTMLButtonElement>}
                css={settingsButtonStyle}
                onClick={openSettingsMenu}
              >
                <HoverableIconComponent
                  icon={faEllipsisV}
                  baseColor={theme.textColors.tertiary}
                  hoverColor={theme.textColors.primary}
                />
              </button>
            )}
          </ContextMenuComponent>
        </div>
      </div>

      {props.error && (
        <div css={errorStyle}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {props.error}
        </div>
      )}
    </div>
  );
};

const ORDER_NUMBER_SIZE = 22;
const ORDER_NUMBER_FONT_SIZE = 14;

const colorTileStyle = (borderColor: string) => css({
  border: `1px solid ${borderColor}`,
  fontSize: ORDER_NUMBER_FONT_SIZE,
});

const polygonWrapperStyle = css({
  display: 'inline-flex',
  height: ORDER_NUMBER_SIZE,
  width: ORDER_NUMBER_SIZE,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
});

const polygonShapeScale = 1.15;
const polygonBorderScale = polygonShapeScale + 0.1;

const polygonBorderStyle = (borderColor: string) => css({
  background: borderColor,
  clipPath: dtpClipPath,
  height: ORDER_NUMBER_SIZE,
  position: 'absolute',
  transform: `scale(${polygonBorderScale})`,
  width: ORDER_NUMBER_SIZE,
});

const polygonShapeStyle = (backgroundColor: string, fontColor: string) => css({
  alignItems: 'center',
  background: backgroundColor,
  clipPath: dtpClipPath,
  color: fontColor,
  cursor: 'default',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: ORDER_NUMBER_FONT_SIZE,
  height: ORDER_NUMBER_SIZE,
  justifyContent: 'center',
  lineHeight: `${ORDER_NUMBER_SIZE}px`,
  position: 'relative',
  transform: `scale(${polygonShapeScale})`,
  width: ORDER_NUMBER_SIZE,
});

const polygonTextStyle = css({
  transform: `scale(${1 / polygonShapeScale})`, // Counter-scale polygonShapeScale
});

const StepNumber: FC<{ number: number; color: string; round: boolean }> = ({ number, color, round }) => {
  const theme = useTheme();

  const [borderColor, fontColor] = getFontAndBorderColorOnBackground(
    createColor(theme.textColors.primary),
    createColor(color),
    createColor(theme.backgroundColors.secondary)
  );

  const circleColors = useMemo(() => ({
    fillColor: color,
    fontColor: guaranteeHash(fontColor.hex),
  }), [color, fontColor]);

  return round ? (
    <LabelInShapeComponent
      colors={circleColors}
      css={colorTileStyle(guaranteeHash(borderColor.hex))}
      size={ORDER_NUMBER_SIZE}
      shape={Shape.circle}
    >
      {number}
    </LabelInShapeComponent>
  ) : (
    <div css={polygonWrapperStyle}>
      <div css={polygonBorderStyle(guaranteeHash(borderColor.hex))} />
      <div css={polygonShapeStyle(color, guaranteeHash(fontColor.hex))}>
        <span css={polygonTextStyle}>{number}</span>
      </div>
    </div>
  );
};
